import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { DelayCausedBy } from 'src/app/services/gantt/gantt.service';
import { DelayType } from '../../gantt/interfaces/gantt-details.model';

export interface DelayReasonDto {
  Id: string;
  Code: string;
  Description?: string;
  IsAvailable?: boolean;
  Type?: number;
}

export interface DelayReasonOption {
  code: string;
  labelKey: string;
}

export interface RemoveLateStatusResponse {
  isSuccess: boolean;
  message?: string;
}

export interface UpdateDelayPayload {
  DelayType: DelayType;
  DelayReasonGuid: string;
}

@Injectable({
  providedIn: 'root',
})
export class DelayedServiceService {
  // API de productos (motivos de retraso)
  private readonly productsUrl: string = environment.products_url;
  private readonly reasonsEndpoint = `${this.productsUrl}/api/delay-reasons`;

  // API core (cambio de estado late del servicio)
  private readonly coreUrl: string = environment.url;
  private readonly removeLateEndpoint = `${this.coreUrl}/gantt/sidebar/remove-late`;

  private readonly trackingUrl: string = environment.trackingUrl;

  private readonly typeByCausedBy: Record<DelayCausedBy, number> = {
    UNDEFINED: 0,
    CLIENT: 1,
    CAFLER: 2,
  };

  private readonly cache: Partial<
    Record<DelayCausedBy, Observable<DelayReasonOption[]>>
  > = {};

  constructor(private readonly http: HttpClient) {}

  getDelayReasons(causedBy: DelayCausedBy): Observable<DelayReasonOption[]> {
    const cached = this.cache[causedBy];
    if (cached) {
      return cached;
    }

    const type = this.typeByCausedBy[causedBy];

    const request$ = this.http
      .get<DelayReasonDto[]>(`${this.reasonsEndpoint}/${type}`)
      .pipe(
        map((items) => this.mapToOptions(items)),
        shareReplay(1),
        catchError((error) => {
          console.error(
            '[DelayedServiceService] getDelayReasons error for',
            causedBy,
            error
          );
          return throwError(() => error);
        })
      );

    this.cache[causedBy] = request$;
    return request$;
  }

  removeServiceDelay(orderHash: string): Observable<void> {
    return this.http.put<void>(
      `${this.trackingUrl}/service/${orderHash}/remove-delay`,
      {}
    );
  }

  updateServiceDelay(
    orderHash: string,
    payload: UpdateDelayPayload
  ): Observable<void> {
    return this.http.put<void>(
      `${this.trackingUrl}/service-delay/${orderHash}`,
      payload
    );
  }

  getDelayReasonDetails(causedBy: DelayCausedBy): Observable<DelayReasonDto[]> {
    const type = this.typeByCausedBy[causedBy];
    return this.http.get<DelayReasonDto[]>(`${this.reasonsEndpoint}/${type}`);
  }

  private mapToOptions(
    dtos: DelayReasonDto[] | null | undefined
  ): DelayReasonOption[] {
    if (!Array.isArray(dtos) || dtos.length === 0) {
      return [];
    }

    return dtos
      .filter((dto) => dto?.Code)
      .map((dto) => ({
        code: dto.Code,
        labelKey: `service_manager.delay.reasons.${dto.Code.toLowerCase()}`,
      }));
  }

  mapToOption(
    dto: DelayReasonDto | null | undefined
  ): DelayReasonOption | null {
    if (!dto) {
      return null;
    }
    return {
      code: dto.Code,
      labelKey: `service_manager.delay.reasons.${dto.Code.toLowerCase()}`,
    };
  }
}
